import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  sendRegistrationConfirmation,
  sendApprovalNotification,
  sendRejectionNotification,
  sendPasswordCreationConfirmation
} from '../utils/emailService';

const TEST_EMAIL = 'vivekkumar787067@gmail.com';

const EmailTestAll = () => {
  const [loading, setLoading] = useState(false);
  const [currentTest, setCurrentTest] = useState('');
  const [results, setResults] = useState([]);

  // Test data matching the structure expected by email functions
  const testInfluencerData = {
    name: 'Vivek Kumar',
    email: TEST_EMAIL,
    instagram: '@vivekkumar',
    influencerType: 'Featured Influencer',
    influencerId: 'INF12345678',
    status: 'approved'
  };

  const sendEmail = async (emailFunction, emailName, testData) => {
    setCurrentTest(emailName);
    setLoading(true);
    try {
      const result = await emailFunction(testData);
      setResults(prev => [...prev, {
        name: emailName,
        success: result.success,
        message: result.success ? 'Email sent successfully!' : result.error,
        timestamp: new Date().toLocaleTimeString()
      }]);
      return result;
    } catch (error) {
      setResults(prev => [...prev, {
        name: emailName,
        success: false,
        message: error.message || 'Failed to send email',
        timestamp: new Date().toLocaleTimeString()
      }]);
      return { success: false, error: error.message };
    } finally {
      setLoading(false);
      setCurrentTest('');
    }
  };

  const sendAllEmails = async () => {
    setResults([]);
    setLoading(true);

    // Send emails one by one with delay
    const emails = [
      {
        func: sendRegistrationConfirmation,
        name: 'Registration Confirmation',
        data: testInfluencerData
      },
      {
        func: sendApprovalNotification,
        name: 'Approval Notification',
        data: testInfluencerData
      },
      {
        func: sendRejectionNotification,
        name: 'Rejection Notification',
        data: { ...testInfluencerData, influencerType: 'Normal' }
      },
      {
        func: sendPasswordCreationConfirmation,
        name: 'Password Creation Confirmation',
        data: testInfluencerData
      }
    ];

    for (let i = 0; i < emails.length; i++) {
      const email = emails[i];
      setCurrentTest(email.name);
      await sendEmail(email.func, email.name, email.data);
      
      // Wait 2 seconds between emails
      if (i < emails.length - 1) {
        await new Promise(resolve => setTimeout(resolve, 2000));
      }
    }

    setLoading(false);
    setCurrentTest('');
  };

  const sendSingleEmail = async (emailFunction, emailName, testData) => {
    await sendEmail(emailFunction, emailName, testData);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100 py-12 px-4">
      <div className="max-w-4xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-2xl shadow-xl p-8 mb-8"
        >
          <h1 className="text-3xl font-bold text-gray-800 mb-2">
            📧 Email Sequence Test Dashboard
          </h1>
          <p className="text-gray-600 mb-4">
            Test all influencer email sequences. Emails will be sent to:
          </p>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <p className="text-lg font-semibold text-blue-800">
              {TEST_EMAIL}
            </p>
          </div>

          {/* Send All Button */}
          <div className="mb-8">
            <button
              onClick={sendAllEmails}
              disabled={loading}
              className={`w-full py-4 px-6 rounded-lg text-white font-semibold text-lg transition-all duration-200 ${
                loading
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-gradient-to-r from-orange-400 via-pink-400 to-purple-400 hover:from-orange-500 hover:via-pink-500 hover:to-purple-500 shadow-lg hover:shadow-xl'
              }`}
            >
              {loading && currentTest ? (
                <span>⏳ Sending {currentTest}...</span>
              ) : loading ? (
                <span>⏳ Processing...</span>
              ) : (
                <span>🚀 Send All Emails (One by One)</span>
              )}
            </button>
          </div>

          {/* Individual Email Buttons */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
            <button
              onClick={() => sendSingleEmail(sendRegistrationConfirmation, 'Registration Confirmation', testInfluencerData)}
              disabled={loading}
              className="py-3 px-4 bg-blue-500 hover:bg-blue-600 text-white rounded-lg font-semibold transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              1️⃣ Registration Confirmation
            </button>
            <button
              onClick={() => sendSingleEmail(sendApprovalNotification, 'Approval Notification', testInfluencerData)}
              disabled={loading}
              className="py-3 px-4 bg-green-500 hover:bg-green-600 text-white rounded-lg font-semibold transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              2️⃣ Approval Notification
            </button>
            <button
              onClick={() => sendSingleEmail(sendRejectionNotification, 'Rejection Notification', { ...testInfluencerData, influencerType: 'Normal' })}
              disabled={loading}
              className="py-3 px-4 bg-red-500 hover:bg-red-600 text-white rounded-lg font-semibold transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              3️⃣ Rejection Notification
            </button>
            <button
              onClick={() => sendSingleEmail(sendPasswordCreationConfirmation, 'Password Creation Confirmation', testInfluencerData)}
              disabled={loading}
              className="py-3 px-4 bg-purple-500 hover:bg-purple-600 text-white rounded-lg font-semibold transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              4️⃣ Password Creation Confirmation
            </button>
          </div>

          {/* Results */}
          {results.length > 0 && (
            <div className="mt-8">
              <h2 className="text-xl font-bold text-gray-800 mb-4">Test Results</h2>
              <div className="space-y-3">
                {results.map((result, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    className={`p-4 rounded-lg border-2 ${
                      result.success
                        ? 'bg-green-50 border-green-200'
                        : 'bg-red-50 border-red-200'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        {result.success ? (
                          <span className="text-2xl">✅</span>
                        ) : (
                          <span className="text-2xl">❌</span>
                        )}
                        <div>
                          <p className="font-semibold text-gray-800">{result.name}</p>
                          <p className={`text-sm ${result.success ? 'text-green-700' : 'text-red-700'}`}>
                            {result.message}
                          </p>
                        </div>
                      </div>
                      <span className="text-xs text-gray-500">{result.timestamp}</span>
                    </div>
                  </motion.div>
                ))}
              </div>

              {/* Summary */}
              <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-600">
                  <strong>Summary:</strong> {results.filter(r => r.success).length} of {results.length} emails sent successfully
                </p>
              </div>
            </div>
          )}

          {/* Test Data Info */}
          <div className="mt-8 p-4 bg-gray-50 rounded-lg">
            <h3 className="font-semibold text-gray-800 mb-2">Test Data Used:</h3>
            <pre className="text-xs text-gray-600 overflow-x-auto">
              {JSON.stringify(testInfluencerData, null, 2)}
            </pre>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default EmailTestAll;

